import React from "react";
import { useState } from "react";
import ReactModal from "react-modal";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { HInput } from "../../../validation/HForm";
import { joiResolver } from "@hookform/resolvers/joi";
import { industrySchema } from "../debtToIncomeRatioShcema";
import { debtHandbookFields, formatNumberInput, modalTitles } from "../helpers";
import EditableField from "../components/EditableField";
import ActionButtons from "../components/ActionButtons";
import CrossIcon from "../../../assets/icons/cross.svg";
import DeleteIcon from "../../../assets/icons/trash.svg";
import "../index.scss";

export const IndustryForm = ({ onSubmit, onClose, defaultValues }) => {
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [amount, setAmount] = useState("");
  const [editingField, setEditingField] = useState(null);
  const [editedValue, setEditedValue] = useState("");

  const form = useForm({
    defaultValues: defaultValues,
    resolver: joiResolver(industrySchema),
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "values",
    keyName: "_id",
  });

  const watchFieldArray = form.watch("values");
  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  const startEditing = (label, value) => {
    setEditingField(label);
    setEditedValue(value);
  };

  const handleAddEntry = () => {
    if (!selectedIndustry || !amount || isNaN(parseFloat(amount))) return;

    append({
      name: selectedIndustry,
      value: parseFloat(amount) || 0,
      id: null
    });

    setSelectedIndustry("");
    setAmount("");
  };

  const handleFormSubmit = (data) => {
    if (editingField !== null) return;
    if (onSubmit) {
      const formattedData = {
        name: debtHandbookFields.AVG_INDUSTRY_INCOME,
        values: data.values
      };
      onSubmit(formattedData);
    }
  };

  const handleSave = (index) => {
    if (editingField === null || editedValue.trim() === "") {
      setEditingField(null);
      setEditedValue(controlledFields[index].name);
      return;
    }
    form.setValue(`values.${editingField}.name`, editedValue);
    setEditingField(null);
  };

  return (
    <ReactModal
      isOpen={true}
      contentLabel="Modal"
      className="modal-container"
      overlayClassName="modal-overlay"
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)}>
          <div className="modal-header">
            <h2>{modalTitles[debtHandbookFields.AVG_INDUSTRY_INCOME]}</h2>
            <button
              className="button-icontrigger modal-closetrigger"
              onClick={onClose}
              type="button"
            >
              <span className="icon">
                <CrossIcon />
              </span>
            </button>
          </div>
          <div className="modal-body modal-body__height debt-modal">
            <div className="form-row custom-grid p-b-2">
              <div className="form-row-item d-flex">
                <div className="fullwidth">
                  <label className="form-element-label">
                    Отрасль
                  </label>
                  <div>
                    <HInput
                      className="input p-t-spacer fullwidth"
                      placeholder="Введите название отрасли"
                      type="text"
                      onChange={(e) => setSelectedIndustry(e.target.value)}
                      value={selectedIndustry}
                    />
                  </div>
                </div>
              </div>

              <div className="d-flex flex-end">
                <div>
                  <label className="form-element-label">
                    Сумма (руб.)
                  </label>
                  <div className="form-element-data number">
                    <HInput
                      className="input p-t-spacer"
                      type="text"
                      autoComplete="off"
                      decimals={2}
                      onChange={(e) => setAmount(e.target.value)}
                      value={amount}
                    />
                  </div>
                </div>
              </div>
              <div className="text-right">
                <button
                  type="button"
                  className="action-item"
                  onClick={handleAddEntry}
                  disabled={!selectedIndustry || !amount}
                >
                  Добавить
                </button>
              </div>
            </div>
            {controlledFields.map((entry, index) => {
              const isEditing = editingField === index;
              return (
                <div key={entry._id} className="form-row p-b-half">
                  <HInput
                      type="hidden"
                      name={`values.${index}.id`}
                      value={entry.id}
                    />
                  <HInput
                    type="hidden"
                    name={`values.${index}.name`}
                    value={entry.name}
                  />
                  <div className="form-row-item d-flex flex-1 industry-field">
                    <EditableField
                      value={entry.name}
                      isEditing={isEditing}
                      editedValue={editedValue}
                      onValueChange={setEditedValue}
                    />
                    <ActionButtons
                      isEditing={isEditing}
                      onSave={() => handleSave(index)}
                      onCancel={() => {
                        setEditingField(null);
                        setEditedValue("");
                      }}
                      onEdit={() => startEditing(index, entry.name)}
                    />
                  </div>
                  <div className="form-element-data form-row-item number align-end">
                    <HInput
                      type="text"
                      h="true"
                      name={`values.${index}.value`}
                      autoComplete="off"
                      className="input"
                      decimals={2}
                      defaultValue={formatNumberInput(entry.value)}
                    />
                  </div>
                  <div className="form-row-item text-right">
                    <button
                      type="button"
                      className="button-icontrigger button-icontrigger-absolute"
                      onClick={() => remove(index)}
                    >
                      <span className="icon">
                        <DeleteIcon />
                      </span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="modal-footer">
            <button className="button" type="submit">
              Сохранить
            </button>
            <button className="button-light" onClick={onClose}>
              Отменить
            </button>
          </div>
        </form>
      </FormProvider>
    </ReactModal>
  );
};
